import {styled} from '@mui/material/styles';
import {filledInputClasses, TextField, Typography, Tooltip, Button} from '@mui/material';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import Autocomplete from '@mui/material/Autocomplete';
import {isString} from 'lodash/fp';
import {useMemo} from 'react';

import {DEFAULT_FILTER_NAME} from 'core/constants/policyFilter';
import {yup} from 'utils/yup';
import {useTemporaryClick} from 'utils/hooks/useTemporaryClick';
import {useEditedFilterContext} from 'filters/features/FilterDialog/contexts/internal/EditedFilterProvider/EditedFilterProvider';
import {useFilterActionsContext} from 'filters/contexts/FilterActionsProvider/FilterActionsProvider';

import SaveSuccessfulIcon from 'components/icons/saveSuccessful.svg';
import SaveIcon from 'components/icons/save.svg';

import {isEqualFilters} from 'modules/decisionsNext/components/BaseFilter/utils/filter';

const schema = yup.object({
  name: yup
    .string()
    .trim()
    .required('Please name your filter before saving')
    .test('non-default', 'Please use another name', value => value !== DEFAULT_FILTER_NAME),
});

type Schema = yup.InferType<typeof schema>;

const SForm = styled('form')(({theme}) => ({
  display: 'grid',
  alignItems: 'start',
  gridTemplateColumns: '1fr max-content',
  columnGap: theme.spacing(2),

  [theme.containerQueries.down(600)]: {
    gridTemplateColumns: '1fr',
  },
}));

const STextField = styled(TextField)(({theme}) => ({
  [`& .${filledInputClasses.root}`]: {
    padding: `${theme.spacing(2, 9, 2, 2)} !important`,

    '> input': {
      padding: '0 !important',
    },
  },
}));

export const SaveFiltersForm = () => {
  const {useStoredFilters, useStoredFiltersActions, useApplyFilter} = useFilterActionsContext();
  const {
    savedLoading,
    savedFilters: {saved, savedWithDefault},
  } = useStoredFilters();
  const {saveFilter} = useStoredFiltersActions();
  const applyFilter = useApplyFilter();

  const {filter} = useEditedFilterContext();
  const similarSaved = useMemo(() => {
    return savedWithDefault?.find(stored => isEqualFilters(stored.filter, filter));
  }, [filter, savedWithDefault]);

  const {
    control,
    formState: {errors, isSubmitting},
    watch,
    handleSubmit,
  } = useForm<Schema>({
    values: {
      name: similarSaved?.name || '',
    },
    resolver: yupResolver(schema),
  });
  const name = watch('name');
  const isUpdate = !!saved?.find(filter => filter.name === name);

  const [clicked, toggleClick] = useTemporaryClick();

  const onSubmit = async (values: Schema) => {
    try {
      const savedFilterId = await saveFilter(filter, values.name);

      if (savedFilterId) {
        await applyFilter(filter, savedFilterId);
      }

      toggleClick();
    } catch {
      /* empty */
    }
  };

  return (
    <div>
      <Typography variant='subtitle2' color='text.primary' marginBottom={2}>
        Save your filters for future easy data analysis (optional)
      </Typography>
      <SForm onSubmit={handleSubmit(onSubmit)}>
        <Tooltip title={similarSaved ? 'You already have a saved filter with the same settings' : undefined}>
          <div>
            <Controller
              disabled={!!similarSaved}
              control={control}
              name='name'
              render={({field}) => (
                <Autocomplete<{id: string; name: string}, false, true, true>
                  freeSolo
                  disabled={field.disabled}
                  forcePopupIcon
                  selectOnFocus
                  disableClearable
                  handleHomeEndKeys
                  loading={savedLoading}
                  inputValue={field.value || ''}
                  onInputChange={(_, value) => field.onChange(value)}
                  onBlur={e => {
                    const value = (e.target as HTMLInputElement).value;

                    value && field.onChange(value);
                    field.onBlur();
                  }}
                  onChange={(_, value) => {
                    if (!value) {
                      return field.onChange('');
                    }

                    if (isString(value)) {
                      return field.onChange(value);
                    }

                    field.onChange(value.name);
                  }}
                  options={saved ?? []}
                  getOptionLabel={option => (isString(option) ? option : option.name)}
                  renderInput={params => (
                    <STextField
                      {...params}
                      size='small'
                      variant='filled'
                      color='secondary'
                      placeholder='Enter a name'
                      error={!!errors.name?.message}
                      helperText={errors.name?.message || <span>&nbsp;</span>}
                      inputProps={{
                        ...params.inputProps,
                        maxLength: 100,
                      }}
                    />
                  )}
                />
              )}
            />
          </div>
        </Tooltip>
        <Tooltip title={similarSaved ? 'You already have a saved filter with the same settings' : undefined}>
          <div>
            <Button
              style={{minWidth: 147}}
              fullWidth
              disabled={!!similarSaved}
              startIcon={clicked ? <SaveSuccessfulIcon /> : <SaveIcon />}
              loading={isSubmitting || savedLoading}
              variant='contained'
              color='secondary'
              type='submit'
            >
              {isUpdate ? (clicked ? 'Updated' : 'Update Filters') : clicked ? 'Saved' : 'Save Filters'}
            </Button>
          </div>
        </Tooltip>
      </SForm>
    </div>
  );
};
